import {useEffect, useState} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {useBreadcrumb} from '../context/breadcrumbs-context';
import {API} from '../lib/axios';
import {Card, CardActions, CardHeader} from "../components/layouts/card-components.tsx";
import InputSelect from "../components/input-select.tsx";
import {useTranslation} from "react-i18next";
import {generatePeriods, Period} from "../lib/periods";
import moment from "moment";
import {TableLoader} from "../components/table-loader.tsx";
import OverviewTable from "../components/overview-table.tsx";

export default function CompanyReportPage() {
    const {id} = useParams();
    const [params] = useSearchParams()
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<CompanyData[]>([]);
    const [unitName, setUnitName] = useState('');
    const [period, setPeriod] = useState<string>(params.get("period") as string)
    const [periods, setPeriods] = useState<Period[]>([]);
    const {t} = useTranslation()
    const metric = params.get("metric") || "revenue"

    // `/reports/42` would otherwise render the company id as a crumb.
    useBreadcrumb(
        unitName
            ? [
                  {label: t('breadcrumbs.label.reports'), href: '/reports'},
                  {label: unitName},
              ]
            : null,
    );

    useEffect(() => {
        setPeriods(generatePeriods());
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setData([]); // Clear existing data before fetching new data

            const year = moment(period).year()
            const month = moment(period).format("MM")
            try {
                const {data} = await API.get(
                    `/network?year=${year}&month=${month}&report=unit_drilldown&metric=${metric}`,
                    {
                        headers: {
                            'X-ZOOZA-NETWORK-COMPANY': id,
                        },
                    }
                );
                setData(data?.data);
                setUnitName(data.meta.unit_name);
            } catch (err) {
                console.error('Failed to fetch company report', err);
                setData([]); // Ensure data is cleared on error
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id, period, metric]);

    const downloadCSV = () => {
        if (data.length === 0) return;

        const headers = ['product_code_id', 'received_payments', 'new_enrollments', 'ended', 'net_revenue', 'revenue', 'enrollments', 'cancellations', 'royalties'].join(',');
        const rows = data.map(item =>
            [
                `"${item.product_code_id}"`,
                `"${item.received_payments}"`,
                `"${item.new_enrollments}"`,
                `"${item.ended}"`,
                `"${item.net_revenue}"`,
                `"${item.revenue}"`,
                `"${item.enrollments}"`,
                `"${item.cancellations}"`,
                `"${item.royalties}"`
            ].join(',')
        ).join('\n');

        const csvContent = `${headers}\n${rows}`;
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');

        if (link.download !== undefined) {
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `company_report_${unitName}_${period}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <div className="app_page_layout support_wide_screens">
            <div className="app_page_body">
                <div style={{gridColumn: 'span 4'}}>
                    <Card>
                        <CardHeader title={unitName} iconClass={"icon-dashboard-2"}/>
                        <div className="card_form">
                            <InputSelect
                                name="period"
                                label={t("report.placeholder.choose_period")}
                                value={period}
                                options={periods}
                                optionsText="name"
                                optionsValue="id"
                                required
                                onChange={(value) => setPeriod(value)}
                            />
                        </div>
                        {loading ? (
                            <TableLoader/>
                        ) : (
                            <OverviewTable data={data} period={period} company_id={Number(id)}/>
                        )}
                        <CardActions>
                            <button
                                onClick={downloadCSV}
                                className="btn btn-primary"
                                disabled={loading || data.length === 0}
                            >
                                {t("download_csv") || "Download CSV"}
                            </button>
                        </CardActions>
                    </Card>
                </div>
            </div>
        </div>
    );
}

export type Meta = {
    lead_metric: string;
    unit_name: string;
}

export type CompanyData = {
    period: string;
    product_code_id: string;
    received_payments: string;
    new_enrollments: number;
    ended: number;
    net_revenue: string;
    revenue: string;
    enrollments: number;
    cancellations: number;
    royalties: string;
}

